import { Core } from "@faire/design-tokens";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { IEmailPreferenceGroup } from "@faire/web-api--source/com/faire/email/preferences/IEmailPreferenceGroup";
import { IEmailPreferenceSubgroup } from "@faire/web-api--source/com/faire/email/preferences/IEmailPreferenceSubgroup";
import { IUpdateEmailPreferencesRequest } from "@faire/web-api--source/com/faire/email/preferences/IUpdateEmailPreferencesRequest";
import getEmailPreferences, {
  QueryParameters as EmailPreferencesParams,
} from "@faire/web-api--source/endpoints/www/api/email/preferences/get";
import postEmailPreferences from "@faire/web-api--source/endpoints/www/api/email/preferences/post";
import { trackRetailersOptInNudgeSignUpCtaSuccess } from "@faire/web-api--source/events/retailers/success/optInNudgeSignUpCta";
import { trackRetailersOptInNudgesViewView } from "@faire/web-api--source/events/retailers/view/optInNudgesView";
import { MARKETING_EMAIL_OPT_IN_SUBMITTED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MARKETING_EMAIL_OPT_IN_SUBMITTED";
import { useStrictLocalization } from "@faire/web/common/localization";
import { logError } from "@faire/web/common/logging";
import { Button } from "@faire/web/slate/Button";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useState } from "@faire/web/ui/hooks/useState";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { optInOrigin } from "../../Banners/OptInNudgeBanners/OptInNudgeInput";

export const OptInNudgeInputWithFooter: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { emailAddress } = useUserStore();
  const { setHasOccurred: setOptInSubmitted } = useMilestone(
    MARKETING_EMAIL_OPT_IN_SUBMITTED
  );

  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSuccessfullyOptedIn, setHasSuccessfullyOptedIn] =
    useState<boolean>(false);

  const ref = useDoActionWhenVisible(() => {
    trackRetailersOptInNudgesViewView(optInOrigin.FOOTER_PAGE);
  });

  const unsubscribeToAllEmailPreferenceGroups = (
    emailPreferenceGroups: IEmailPreferenceGroup[]
  ): IEmailPreferenceGroup[] => {
    const emailPreferenceGroupsClone = emailPreferenceGroups;

    emailPreferenceGroupsClone.forEach(
      (emailPreferenceGroup: IEmailPreferenceGroup) => {
        emailPreferenceGroup.subgroups.forEach(
          (subgroup: IEmailPreferenceSubgroup) => {
            subgroup.is_unsubscribed = false;
          }
        );
      }
    );

    return emailPreferenceGroupsClone;
  };

  const otherError = strictLocalize({
    defaultMessage: "Something went wrong—please try again.",
    description: {
      text: "Fallback error message given after submitting an email address to opt into marketing emails",
      img: "https://cdn.faire.com/fastly/d903afa775cffef408d6aef49c31436919b9fce1c06bda893913d873dd40ba8a.png",
    },
  });

  const handleEmailSubmission = async () => {
    setError(undefined);
    setIsLoading(true);

    try {
      const { email_preference_groups } = await getEmailPreferences(
        EmailPreferencesParams.build({
          outgoing_email: emailAddress,
        })
      );

      if (!email_preference_groups) {
        setError(otherError);
        return;
      }

      await postEmailPreferences(
        IUpdateEmailPreferencesRequest.build({
          email_preference_groups: unsubscribeToAllEmailPreferenceGroups(
            email_preference_groups
          ),
          unsubscribe_from_all: false,
        })
      );

      trackRetailersOptInNudgeSignUpCtaSuccess(optInOrigin.FOOTER_PAGE);

      setOptInSubmitted();
      setHasSuccessfullyOptedIn(true);
    } catch (e) {
      logError(e);
      setError(otherError);
    } finally {
      setIsLoading(false);
    }
  };

  if (hasSuccessfullyOptedIn) {
    return (
      <Typography>
        <StrictLocalMsg
          defaultMessage="You’re in! Look for your first email soon."
          description={{
            text: "Opt-in nudge banner: after successful email submission",
            img: "https://cdn.faire.com/fastly/8aaa79b6fd906b9f33858493a6ddaba2e28822be85ff1738cc82c230ac9b01d9.png",
          }}
        />
      </Typography>
    );
  }

  if (!emailAddress) {
    return null;
  }

  return (
    <div ref={ref}>
      <Typography>
        <StrictLocalMsg
          defaultMessage="We'll send updates to {emailAddress}"
          description={{
            text: "Opt-in nudge banner: email input label",
            img: "https://cdn.faire.com/fastly/2cc4e9fab5560e9a073737112b77addfa0cef6d87c330142b19fd69f942d00c5.png",
          }}
          values={{ emailAddress: emailAddress }}
        />
      </Typography>
      <Spacer height="4x" />
      {error ? (
        <>
          <Typography variant="labelSansRegular" color={Core.text.critical}>
            {error}
          </Typography>
          <Spacer height="1x" />
        </>
      ) : null}
      <Button
        variant="secondary"
        loading={isLoading}
        onClick={handleEmailSubmission}
      >
        <StrictLocalMsg
          defaultMessage="Opt in to email"
          description={{
            text: "Opt-in nudge banner: marketing emails sign up CTA",
            img: "https://cdn.faire.com/fastly/2cc4e9fab5560e9a073737112b77addfa0cef6d87c330142b19fd69f942d00c5.png",
          }}
        />
      </Button>
    </div>
  );
};
